import {
  type Dispatch,
  type ReactElement,
  type SetStateAction,
  useMemo,
  useState,
} from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { type LucideIcon, Mail, MapPin, Phone, Trash2Icon } from 'lucide-react';

import type { GetUserQuery } from '@graphql/__generated__/graphql';
import { ListingAccordion } from '@modules/_shared/components/ListingAccordion';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import {
  formatPhone,
  formatPostcodeLocality,
} from '@modules/_shared/utils/formatting';
import {
  AddressEditForm,
  type HomeAddressFields,
} from '@modules/users/user-overview/components/AddressEditForm';
import { CardAddMenu } from '@modules/users/user-overview/components/CardAddMenu';
import { ContactEditForm } from '@modules/users/user-overview/components/ContactEditForm';
import { EditableContactRow } from '@modules/users/user-overview/components/EditableContactRow';
import { EditableLanguageRow } from '@modules/users/user-overview/components/EditableLanguageRow';
import { useCanViewSubscriptions } from '@modules/users/user-overview/hooks/useCanViewSubscriptions';
import { useExpandableCard } from '@modules/users/user-overview/hooks/useExpandableCard';
import {
  DELETE_USER_EMAIL,
  DELETE_USER_PHONE_NUMBER,
  GET_USER,
  INSERT_USER_EMAIL,
  INSERT_USER_PHONE_NUMBER,
  SET_USER_HOME_ADDRESS,
  UPDATE_PRIMARY_USER_EMAIL,
  UPDATE_PRIMARY_USER_PHONE_NUMBER,
  UPDATE_USER_EMAIL,
  UPDATE_USER_PHONE_NUMBER,
} from '@modules/users/userQueries';
import { useLocale } from '@shared/hooks/locale';

const withPendingId = <K,>(
  setter: Dispatch<SetStateAction<ReadonlySet<K>>>,
  key: K,
  promise: Promise<unknown>,
): Promise<void> => {
  setter(prev => new Set(prev).add(key));
  return promise
    .then(
      () => {},
      () => {},
    )
    .finally(() =>
      setter(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      }),
    );
};

type UserContactCardProps = {
  user: NonNullable<GetUserQuery['users_by_pk']>;
};

type ContactFieldItem = {
  id: string;
  value: string;
  primary?: boolean;
  verified?: boolean;
  display?: ReactElement;
};

type ContactFieldSectionProps = {
  icon: LucideIcon;
  kind: 'email' | 'phone';
  items: ContactFieldItem[];
  editingId: string | null;
  onEdit: (id: string) => void;
  onCancelEdit: () => void;
  onUpdate: (id: string, value: string) => Promise<void>;
  isUpdating: boolean;
  onDelete: (id: string) => void;
  onMakePrimary?: (id: string) => Promise<void>;
  onToggleVerified?: (id: string, currentVerified?: boolean) => Promise<void>;
  pendingVerifyIds: ReadonlySet<string>;
  primaryPending: boolean;
  adding: boolean;
  onAdd: (value: string) => Promise<void>;
  isAdding: boolean;
  onCancelAdd: () => void;
};

const ContactFieldSection = ({
  icon,
  kind,
  items,
  editingId,
  onEdit,
  onCancelEdit,
  onUpdate,
  isUpdating,
  onDelete,
  onMakePrimary,
  onToggleVerified,
  pendingVerifyIds,
  primaryPending,
  adding,
  onAdd,
  isAdding,
  onCancelAdd,
}: ContactFieldSectionProps) => (
  <>
    {items.map(item =>
      editingId === item.id ? (
        <ContactEditForm
          key={item.id}
          icon={icon}
          kind={kind}
          initialValue={item.value}
          isSubmitting={isUpdating}
          onCancel={onCancelEdit}
          onSubmit={async value => {
            await onUpdate(item.id, value);
            onCancelEdit();
          }}
        />
      ) : (
        <EditableContactRow
          key={item.id}
          icon={icon}
          value={item.value}
          display={item.display}
          primary={item.primary}
          verified={item.verified}
          onEdit={() => onEdit(item.id)}
          onDelete={() => onDelete(item.id)}
          onMakePrimary={
            onMakePrimary ? () => onMakePrimary(item.id) : undefined
          }
          onToggleVerified={
            onToggleVerified
              ? () => onToggleVerified(item.id, item.verified)
              : undefined
          }
          verifyPending={pendingVerifyIds.has(item.id)}
          primaryPending={primaryPending}
        />
      ),
    )}
    {adding && (
      <ContactEditForm
        icon={icon}
        kind={kind}
        isSubmitting={isAdding}
        onCancel={onCancelAdd}
        onSubmit={async value => {
          await onAdd(value);
          onCancelAdd();
        }}
      />
    )}
  </>
);

export const UserContactCard = ({ user }: UserContactCardProps) => {
  const { t } = useLocale();
  const { me } = useAppData();
  const isAdmin = me?.is_admin ?? false;
  const canViewSubscriptions = useCanViewSubscriptions(user.id);

  const { expand, accordionProps } = useExpandableCard();
  const [apolloError, setApolloError] = useState<ErrorLike | null>(null);
  const [editingEmailId, setEditingEmailId] = useState<string | null>(null);
  const [editingPhoneId, setEditingPhoneId] = useState<string | null>(null);
  const [addingEmail, setAddingEmail] = useState(false);
  const [addingPhone, setAddingPhone] = useState(false);
  const [editingAddress, setEditingAddress] = useState(false);

  const mutationOpts = useMemo(
    () => ({
      refetchQueries: [
        {
          query: GET_USER,
          variables: {
            id: user.id,
            includeSubscriptions: canViewSubscriptions,
          },
        },
      ],
      awaitRefetchQueries: true,
      onError: setApolloError,
    }),
    [user.id, canViewSubscriptions],
  );

  const [insertUserEmail, { loading: insertingEmail }] = useMutation(
    INSERT_USER_EMAIL,
    mutationOpts,
  );
  const [updateUserEmail, { loading: updatingEmail }] = useMutation(
    UPDATE_USER_EMAIL,
    mutationOpts,
  );
  const [deleteUserEmail] = useMutation(DELETE_USER_EMAIL, mutationOpts);
  const [updatePrimaryUserEmail] = useMutation(
    UPDATE_PRIMARY_USER_EMAIL,
    mutationOpts,
  );

  const [insertPhone, { loading: insertingPhone }] = useMutation(
    INSERT_USER_PHONE_NUMBER,
    mutationOpts,
  );
  const [updatePhone, { loading: updatingPhone }] = useMutation(
    UPDATE_USER_PHONE_NUMBER,
    mutationOpts,
  );
  const [deletePhone] = useMutation(DELETE_USER_PHONE_NUMBER, mutationOpts);
  const [updatePrimaryPhone] = useMutation(
    UPDATE_PRIMARY_USER_PHONE_NUMBER,
    mutationOpts,
  );

  const [setHomeAddress, { loading: savingAddress }] = useMutation(
    SET_USER_HOME_ADDRESS,
    mutationOpts,
  );

  const emails = user.emails ?? [];
  const phoneNumbers = user.phone_numbers ?? [];

  const addressLine = [user.home_address_street_number, user.home_address_route]
    .filter(Boolean)
    .join(' ');
  const addressCity = formatPostcodeLocality(
    user.home_address_postcode,
    user.home_address_locality,
  );
  const formattedAddress = [addressLine, addressCity]
    .filter(Boolean)
    .join(', ');
  const hasAddress =
    Boolean(user.home_address_route) ||
    Boolean(user.home_address_street_number) ||
    Boolean(user.home_address_postcode) ||
    Boolean(user.home_address_locality) ||
    Boolean(user.home_address_state) ||
    Boolean(user.home_address_country_code);

  const clearedAddress: HomeAddressFields = {
    home_address_route: null,
    home_address_street_number: null,
    home_address_postcode: null,
    home_address_locality: null,
    home_address_state: null,
    home_address_country_code: null,
    home_address_lat: null,
    home_address_lng: null,
  };

  const handleSubmitAddress = (fields: HomeAddressFields) => {
    void setHomeAddress({ variables: { id: user.id, user: fields } }).then(
      () => setEditingAddress(false),
      () => {},
    );
  };

  const [pendingVerify, setPendingVerify] = useState<ReadonlySet<string>>(
    new Set(),
  );
  const runVerify = (id: string, promise: Promise<unknown>) =>
    withPendingId(setPendingVerify, id, promise);

  const [pendingPrimary, setPendingPrimary] = useState<
    ReadonlySet<'email' | 'phone'>
  >(new Set());
  const runPrimary = (kind: 'email' | 'phone', promise: Promise<unknown>) =>
    withPendingId(setPendingPrimary, kind, promise);

  const [pendingDelete, setPendingDelete] = useState<{
    id: string;
    kind: 'email' | 'phone';
    message: string;
  } | null>(null);

  const handleConfirmDelete = async () => {
    if (!pendingDelete) {
      return;
    }
    const { id, kind } = pendingDelete;
    setPendingDelete(null);

    const isEmail = kind === 'email';
    const items: { id: string; primary?: boolean | null }[] = isEmail
      ? emails
      : phoneNumbers;
    const wasPrimary = items.find(i => i.id === id)?.primary ?? false;
    const next = items.find(i => i.id !== id);

    await (isEmail
      ? deleteUserEmail({ variables: { id } })
      : deletePhone({ variables: { id } }));

    if (wasPrimary && next != null) {
      await (isEmail
        ? updatePrimaryUserEmail({
            variables: { user_id: user.id, email_id: next.id },
          })
        : updatePrimaryPhone({
            variables: { user_id: user.id, phone_id: next.id },
          }));
    }
  };

  return (
    <ListingAccordion
      title={t('Contact')}
      {...accordionProps}
      headerAction={
        <CardAddMenu
          tooltip={t('Add contact info')}
          options={[
            {
              id: 'email',
              label: t('Add email'),
              icon: Mail,
              onClick: () => {
                expand();
                setAddingEmail(true);
                setEditingEmailId(null);
              },
            },
            {
              id: 'phone',
              label: t('Add phone'),
              icon: Phone,
              onClick: () => {
                expand();
                setAddingPhone(true);
                setEditingPhoneId(null);
              },
            },
            ...(hasAddress
              ? []
              : [
                  {
                    id: 'address',
                    label: t('Add address'),
                    icon: MapPin,
                    onClick: () => {
                      expand();
                      setEditingAddress(true);
                    },
                  },
                ]),
          ]}
        />
      }
    >
      <Stack sx={{ gap: 0.75 }}>
        <ContactFieldSection
          icon={Mail}
          kind="email"
          pendingVerifyIds={pendingVerify}
          primaryPending={pendingPrimary.has('email')}
          items={emails.map(e => ({
            id: e.id,
            value: e.email,
            primary: e.primary,
            verified: e.verified,
          }))}
          editingId={editingEmailId}
          onEdit={setEditingEmailId}
          onCancelEdit={() => setEditingEmailId(null)}
          onUpdate={async (id, value) => {
            await updateUserEmail({ variables: { id, set: { email: value } } });
          }}
          isUpdating={updatingEmail}
          onDelete={id => {
            const email = emails.find(e => e.id === id);
            setPendingDelete({
              id,
              kind: 'email',
              message: t('Delete email "{{value}}"?', {
                value: email?.email ?? '',
              }),
            });
          }}
          onMakePrimary={id =>
            runPrimary(
              'email',
              updatePrimaryUserEmail({
                variables: { user_id: user.id, email_id: id },
              }),
            )
          }
          onToggleVerified={
            isAdmin
              ? (id, currentVerified) =>
                  runVerify(
                    id,
                    updateUserEmail({
                      variables: { id, set: { verified: !currentVerified } },
                    }),
                  )
              : undefined
          }
          adding={addingEmail}
          onAdd={async value => {
            await insertUserEmail({
              variables: {
                input: {
                  user_id: user.id,
                  email: value,
                  primary: emails.length === 0,
                },
              },
            });
          }}
          isAdding={insertingEmail}
          onCancelAdd={() => setAddingEmail(false)}
        />

        <ContactFieldSection
          icon={Phone}
          kind="phone"
          pendingVerifyIds={pendingVerify}
          primaryPending={pendingPrimary.has('phone')}
          items={phoneNumbers.map(p => ({
            id: p.id,
            value: p.number ?? '',
            primary: p.primary ?? undefined,
            verified: p.verified ?? undefined,
            display: (
              <Typography
                variant="body2"
                noWrap
                sx={{ color: 'text.secondary' }}
              >
                {formatPhone(p.number) ?? p.number}
              </Typography>
            ),
          }))}
          editingId={editingPhoneId}
          onEdit={setEditingPhoneId}
          onCancelEdit={() => setEditingPhoneId(null)}
          onUpdate={async (id, value) => {
            await updatePhone({ variables: { id, set: { number: value } } });
          }}
          isUpdating={updatingPhone}
          onDelete={id => {
            const phone = phoneNumbers.find(p => p.id === id);
            setPendingDelete({
              id,
              kind: 'phone',
              message: t('Delete phone "{{value}}"?', {
                value: formatPhone(phone?.number) ?? phone?.number ?? '',
              }),
            });
          }}
          onMakePrimary={id =>
            runPrimary(
              'phone',
              updatePrimaryPhone({
                variables: { user_id: user.id, phone_id: id },
              }),
            )
          }
          onToggleVerified={
            isAdmin
              ? (id, currentVerified) =>
                  runVerify(
                    id,
                    updatePhone({
                      variables: { id, set: { verified: !currentVerified } },
                    }),
                  )
              : undefined
          }
          adding={addingPhone}
          onAdd={async value => {
            await insertPhone({
              variables: {
                input: {
                  user_id: user.id,
                  number: value,
                  primary: phoneNumbers.length === 0,
                },
              },
            });
          }}
          isAdding={insertingPhone}
          onCancelAdd={() => setAddingPhone(false)}
        />

        <EditableLanguageRow
          userId={user.id}
          language={user.language ?? null}
        />

        {editingAddress ? (
          <AddressEditForm
            loading={savingAddress}
            onSubmit={handleSubmitAddress}
            onCancel={() => setEditingAddress(false)}
          />
        ) : (
          hasAddress && (
            <EditableContactRow
              icon={MapPin}
              value={formattedAddress}
              isLoading={savingAddress}
              onEdit={() => {
                expand();
                setEditingAddress(true);
              }}
              onDelete={() => handleSubmitAddress(clearedAddress)}
            />
          )
        )}
      </Stack>

      <Dialog
        open={pendingDelete != null}
        onClose={() => setPendingDelete(null)}
      >
        <DialogTitle>{t('Delete')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{pendingDelete?.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDelete(null)}>{t('Cancel')}</Button>
          <Button
            color="error"
            variant="contained"
            disableElevation
            startIcon={<Trash2Icon />}
            onClick={handleConfirmDelete}
          >
            {t('Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setApolloError(null)}
      />
    </ListingAccordion>
  );
};
